import { Injectable } from '@angular/core';
import { MeasuresLoaderService } from '@library/store/measures/measures-loader.service';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { ConsumptionType, SourceType } from '@library/store/modules/modules.interface';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { ControlPageStore } from '@pages/control-page/control-page-store.service';
import { combineLatest, distinctUntilChanged, firstValueFrom } from 'rxjs';

@Injectable()
export class GraphMeasuresLoaderService {

  constructor(
    private controlPageStore: ControlPageStore,
    private measuresLoaderService: MeasuresLoaderService,
    private modulesFacade: ModulesFacade,
  ) {}

  subscribe() {
    combineLatest({
      card: this.controlPageStore.card$.pipe(isDefined()),
      state: this.controlPageStore.state$.pipe(
        distinctUntilChanged((previous, current) => {
          return current.scale === previous.scale &&
            current.timeRange === previous.timeRange &&
            current.options.graphMode === previous.options.graphMode;
        })
      ),
    }).subscribe(() => this.load());
  }

  async load() {
    const card = await firstValueFrom(this.controlPageStore.card$);
    const state = await firstValueFrom(this.controlPageStore.state$);
    if (!state.scale || !state.timeRange) {
      return;
    }

    const datasets =  card.datasets[state.options.graphMode];

    const moduleDatasets = datasets.filter(dataset => dataset.module);
    const roomDatasets = datasets.filter(dataset => dataset.room);

    this.measuresLoaderService.loadModulesMeasures(
      moduleDatasets.map(dataset => dataset.module),
      moduleDatasets.reduce((acc, curr) => {
        if (! acc[curr.module.id]) {
          acc[curr.module.id] = [];
        }

        acc[curr.module.id].push(curr.measureType);

        if (curr.measureType === EnumMeasureTypes.SUM_BOILER_ON) {
          acc[curr.module.id].push(EnumMeasureTypes.SUM_BOILER_OFF);
        }
        return acc;
      }, {}),
      this.controlPageStore.scaleToApiScale(state.scale),
      state.timeRange,
    );

    this.measuresLoaderService.loadRoomsMeasures(
      roomDatasets.map(dataset => dataset.room),
      roomDatasets.reduce((acc, curr) => {
        if (! acc[curr.room.id]) {
          acc[curr.room.id] = [];
        }

        acc[curr.room.id].push(curr.measureType);
        return acc;
      }, {}),
      this.controlPageStore.scaleToApiScale(state.scale),
      state.timeRange,
    );

    this.measuresLoaderService.loadHomeElecMeasures(
      [
        EnumMeasureTypes.SUM_ENERGY_ELEC_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_ELEC_BASE,
        EnumMeasureTypes.SUM_ENERGY_ELEC_PEAK,
        EnumMeasureTypes.SUM_ENERGY_ELEC_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_PRICE_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_PRICE_BASE,
        EnumMeasureTypes.SUM_ENERGY_PRICE_PEAK,
        EnumMeasureTypes.SUM_ENERGY_PRICE_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_PEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_OFFPEAK,
      ],
      this.controlPageStore.scaleToApiScale(state.scale),
      state.timeRange,
    );

    const appliances = await firstValueFrom(this.modulesFacade.appliances$);
    this.measuresLoaderService.loadModulesMeasures(
      appliances,
      appliances.reduce((acc, curr) => {
        acc = {
          ...acc,
          [curr.id]: [
            EnumMeasureTypes.SUM_ENERGY_ELEC_NO_CONTRACT,
            EnumMeasureTypes.SUM_ENERGY_ELEC_BASE,
            EnumMeasureTypes.SUM_ENERGY_ELEC_PEAK,
            EnumMeasureTypes.SUM_ENERGY_ELEC_OFFPEAK,
            EnumMeasureTypes.SUM_ENERGY_PRICE_NO_CONTRACT,
            EnumMeasureTypes.SUM_ENERGY_PRICE_BASE,
            EnumMeasureTypes.SUM_ENERGY_PRICE_PEAK,
            EnumMeasureTypes.SUM_ENERGY_PRICE_OFFPEAK,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_NO_CONTRACT,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PEAK,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_OFFPEAK,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_NO_CONTRACT,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_PEAK,
            EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_OFFPEAK,
          ],
        };
        return acc;
      }, {}),
      this.controlPageStore.scaleToApiScale(state.scale),
      state.timeRange,
    );

    const modules = await firstValueFrom(this.modulesFacade.currentHomeModules$);
    const fluidLines = modules.filter(m => m.consumption_type === ConsumptionType.FLUID);
    this.measuresLoaderService.loadModulesMeasures(
      fluidLines,
      fluidLines.reduce((acc, curr) => {
        let mType: EnumMeasureTypes[];
        switch (curr.source_type) {
          case SourceType.COLD_WATER:
            mType = [
              EnumMeasureTypes.SUM_FLUID_CONSUMPTION_COLD_WATER,
              EnumMeasureTypes.SUM_FLUID_PRICE_COLD_WATER,
            ];
            break;
          case SourceType.HOT_WATER:
            mType = [
              EnumMeasureTypes.SUM_FLUID_CONSUMPTION_HOT_WATER,
              EnumMeasureTypes.SUM_FLUID_PRICE_HOT_WATER,
            ];
            break;
          case SourceType.GAS:
            mType = [
              EnumMeasureTypes.SUM_FLUID_CONSUMPTION_GAS,
              EnumMeasureTypes.SUM_FLUID_PRICE_GAS,
            ];
            break;
        }
        if (typeof mType !== 'undefined') {
          acc = {
            ...acc,
            [curr.id]: mType
          };
        }

        return acc;
      }, {}),
      this.controlPageStore.scaleToApiScale(state.scale),
      state.timeRange,
    );
  }

}
